import {
  Box, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ApiError, GoneErrorCode, NotFoundErrorCode, UnauthorizedErrorCodes,
} from '../../utils/errors';

interface ErrorPageProps {
  errorType: ApiError;
  link?: string;
}

const getErrorPageMessages = (errorType: ApiError) => {
  switch (errorType.code) {
    case NotFoundErrorCode.SettingsNotFound:
      return {
        headerTranslationKey: 'application_not_found_message',
        buttonTranslationKey: 'go_to_credit_pulse',
      };
    case NotFoundErrorCode.SubmissionNotFound:
      return {
        headerTranslationKey: 'submission_not_found_message',
        buttonTranslationKey: 'return_to_application',
      };
    case NotFoundErrorCode.TradeReferenceNotFound:
      return {
        headerTranslationKey: 'trade_reference_not_found_message',
        buttonTranslationKey: 'go_to_credit_pulse',
      };
    case GoneErrorCode.TradeReferenceRequest:
      return {
        headerTranslationKey: 'trade_reference_no_longer_required_message',
        buttonTranslationKey: 'go_to_credit_pulse',
      };
    case NotFoundErrorCode.AdditionalTradeReferencesNotFound:
      return {
        headerTranslationKey: 'additional_trade_references_not_found_message',
        buttonTranslationKey: 'return_to_application',
      };
    case UnauthorizedErrorCodes.AdditionalTradeReferencesUnauthorized:
      return {
        headerTranslationKey: 'additional_trade_references_unauthorized_message',
        buttonTranslationKey: 'return_to_application',
      };
    default:
      // Handle unauthorized errors without specific code (invalid/wrong token)
      if (errorType.status === 401) {
        return {
          headerTranslationKey: 'generic_not_found_message',
          buttonTranslationKey: 'return_to_application',
        };
      }
      return {
        headerTranslationKey: 'generic_not_found_message',
        buttonTranslationKey: 'go_to_credit_pulse',
      };
  }
};

const ErrorPage = ({
  errorType,
  link = 'https://creditpulse.com',
}: ErrorPageProps) => {
  const { t } = useTranslation('components', { keyPrefix: 'status-pages' });
  const theme = useTheme();
  const [ isLoading, setIsLoading ] = useState(false);

  const { headerTranslationKey, buttonTranslationKey } = getErrorPageMessages(errorType);

  return (
    <Box
      sx={{
        width: '100%',
        flexGrow: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 5,
      }}
    >
      <Box
        sx={{
          padding: '2rem 3rem',
          textAlign: 'center',
        }}
      >
        <Typography
          fontSize='1.4rem'
          sx={{ mb: 2 }}
          variant='subtitle1'
        >
          {t(headerTranslationKey)}
        </Typography>
        <Link
          style={{
            display: 'inline-block',
            color: 'white',
            backgroundColor: theme.palette.primary.main,
            textDecoration: 'none',
            padding: '4px 12px',
            borderRadius: '4px',
            fontWeight: 500,
            lineHeight: 2.2,
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            opacity: isLoading ? 0.6 : 1,
            pointerEvents: isLoading ? 'none' : 'auto',
            cursor: isLoading ? 'not-allowed' : 'pointer',
          }}
          to={link}
          onClick={() => setIsLoading(true)}
        >
          {t(buttonTranslationKey)}
        </Link>
      </Box>
    </Box>
  );
};

export default ErrorPage;
